import { useCallback, useMemo, type ReactNode } from "react";
import {
  AreaSeries,
  LineSeries,
  type AreaData,
  type ChartOptions,
  type DeepPartial,
  type IChartApi,
  type LineData,
  type UTCTimestamp,
} from "lightweight-charts";
import type { Currency, PortfolioPeriod, PortfolioTimeseriesResponse } from "@richrich/contracts";
import { ChartFrame } from "./ChartFrame";
import { PeriodSwitch } from "./PeriodSwitch";
import { normalizeByTime, toUtcTime, useLightweightChart } from "./lw";
import { hexToRgba, paletteFor } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { Stat, directionFromSign } from "../primitives/Stat";
import { decimalToNumber, formatCompactMoney, formatMoney, formatPercent } from "../lib/format";

export interface PortfolioValueChartProps {
  data: PortfolioTimeseriesResponse;
  /** Controlled selected period; falls back to `data.period`. */
  period?: PortfolioPeriod;
  onPeriodChange?: (period: PortfolioPeriod) => void;
  /** Area (default) or plain line. */
  variant?: "area" | "line";
  theme?: Theme;
  height?: number;
  loading?: boolean;
  title?: ReactNode;
  className?: string;
}

/**
 * Portfolio value over time (ARCHITECTURE §8) — a lightweight-charts v5 area (or
 * line) series with a period switch. The hero shows the latest exact balance
 * (string-formatted, no float) and a window % change (a ratio, legitimately
 * numeric); the price axis uses compact tick labels.
 */
export function PortfolioValueChart({
  data,
  period,
  onPeriodChange,
  variant = "area",
  theme,
  height = 280,
  loading = false,
  title = "Portfolio value",
  className,
}: PortfolioValueChartProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);
  const currency = data.baseCurrency as Currency;
  const selectedPeriod = period ?? data.period;

  const seriesData = useMemo(
    () =>
      normalizeByTime(
        data.points.map((p) => ({
          time: toUtcTime(p.t),
          value: decimalToNumber(p.value),
        })),
      ),
    [data.points],
  );

  const render = useCallback(
    (chart: IChartApi) => {
      const color = palette.categorical[0]!;
      if (variant === "line") {
        const series = chart.addSeries(LineSeries, {
          color,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        series.setData(seriesData as LineData<UTCTimestamp>[]);
      } else {
        const series = chart.addSeries(AreaSeries, {
          lineColor: color,
          topColor: hexToRgba(color, 0.28),
          bottomColor: hexToRgba(color, 0.02),
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: true,
        });
        series.setData(seriesData as AreaData<UTCTimestamp>[]);
      }
    },
    [palette, seriesData, variant],
  );

  const options = useMemo<DeepPartial<ChartOptions>>(
    () => ({ localization: { priceFormatter: (v: number) => formatCompactMoney(v, currency) } }),
    [currency],
  );

  const { containerRef } = useLightweightChart({ theme: resolved, height, render, options });

  const first = data.points[0];
  const last = data.points[data.points.length - 1];
  const firstNum = first ? decimalToNumber(first.value) : 0;
  const lastNum = last ? decimalToNumber(last.value) : 0;
  const diff = lastNum - firstNum;
  const ratio = firstNum !== 0 ? diff / firstNum : 0;
  const sign = diff > 0 ? 1 : diff < 0 ? -1 : 0;

  const empty = data.points.length === 0;

  return (
    <ChartFrame
      title={title}
      actions={<PeriodSwitch value={selectedPeriod} onChange={(p) => onPeriodChange?.(p)} />}
      height={height}
      loading={loading}
      empty={empty}
      className={className}
    >
      <div className="mb-3">
        <Stat
          label="Latest"
          size="lg"
          value={last ? formatMoney(last.value, currency) : "—"}
          delta={{
            text: formatPercent(ratio, 2, true),
            direction: directionFromSign(sign),
            hint: "over period",
          }}
        />
      </div>
      <div ref={containerRef} style={{ height }} />
    </ChartFrame>
  );
}
